import { filterPropertyTypes, measurementMetrics, programStatuses, programTypes } from 'boot/globals';
import { chain, chunk, get, map, replace, some } from 'lodash';
import { mapState } from 'pinia';
import ProgramsDB from 'src/fb/programs-db';
import ProgramTargetsDB from 'src/fb/programTargets-db';
import Activities from 'src/mixins/Activities';
import Insights from 'src/mixins/Insights';
import Interactions from 'src/mixins/Interactions';
import { dataFields } from 'src/store/dataFields';
import { programs as programsStore } from 'src/store/programs';
import { treatmentPlans } from 'src/store/treatmentPlans';
import { logEvent } from 'src/utils/LogEvent';

export default {

  mixins: [Insights, Activities, Interactions],
  data() {
    return {
      defaultGraphSettings: {
        simple: {
          dataSource: 'dsSessionData',
          dataSeriesPreset: 'spSuccessRate',
          xAxis: 'xaDateDaily',
        },
        frequency: {
          dataSource: 'dsSessionData',
          dataSeriesPreset: 'spFrequencyOccurencesPerHour',
          xAxis: 'xaDateDaily',
        },
        duration: {
          dataSource: 'dsSessionData',
          dataSeriesPreset: 'spTotalDuration',
          xAxis: 'xaDateDaily',
        },
        taskAnalysis: {
          dataSource: 'dsSessionData',
          dataSeriesPreset: 'spSuccessRate',
          xAxis: 'xaDateDaily',
        },
        interval: {
          dataSource: 'dsSessionData',
          dataSeriesPreset: 'spSuccessRate',
          xAxis: 'xaDateDaily',
        },
        behaviour: {
          dataSource: 'dsBehaviorIncidentDataFlat',
          dataSeriesPreset: 'spOccurrences',
          xAxis: 'xaDateDaily',
          groupByDataProperty: null,
          groupBySeriesLabelFrom: null,
        },
        programActivityInfo: {
          originalSession: null,
          session: null,
        },
      },
    };
  },

  computed: {
    ...mapState(programsStore, ['programs']),
    ...mapState(treatmentPlans, ['clinicTreatmentAreas']),
    ...mapState(dataFields, ['visibleProgramDataFields']),

    programStatuses() {
      return map(
        programStatuses,
        (programStatus) => (
          {
            ...programStatus,
            label: this.$t(programStatus.label),
          }
        ),
      );
    }, // programStatuses()

    /**
     * @deprecated Use composables/columnDefinitions/programColumns.js instead
     */
    programColumns() {
      const statusSortOrder = {
        baseline: 0,
        active: 1,
        maintenance: 2,
        hold: 3,
        waiting: 4,
        closed: 5,
        discontinued: 6,
      };
      const statusSortFunction = (a, b, rowA, rowB) => {
        const statusSort = get(statusSortOrder, a, 0) - get(statusSortOrder, b, 0);
        if (statusSort !== 0) {
          return statusSort;
        }

        const programNameA = rowA.programName?.toString() || '';
        const programNameB = rowB.programName?.toString() || '';

        return programNameA.localeCompare(programNameB, undefined, { numeric: true, sensitivity: 'base' });
      }; // statusSortFunction()

      const usedProgramTags = chain(this.programs)
        .map('tags')
        .flatten()
        .uniq()
        .compact()
        .value();

      const filterItemsProgramTags = chain(usedProgramTags)
        .map((tag) => ({ value: tag, label: tag }))
        .orderBy([(tag) => tag.label.toLowerCase()], ['asc'])
        .value();

      return [
        {
          name: 'title',
          required: true,
          label: this.$t('programs.columnsProgramLabel'),
          align: 'left',
          field: (row) => row.title,
          format: (val) => `${val}`,
          sortable: true,
          sort: (a, b) => {
            const stringifiedA = a?.toString() || '';
            const stringifiedB = b?.toString() || '';

            return stringifiedA.localeCompare(stringifiedB, undefined, { numeric: true, sensitivity: 'base' });
          },
          classes: 'truncate-label-300',
          persistent: true,
          mandatory: true,
        },
        {
          name: 'tags',
          align: 'left',
          label: this.$t('programs.columnsTagsLabel'),
          field: 'tags',
          classes: 'white-space-normal',
          sortable: true,

          filterable: true,
          filterType: filterPropertyTypes.ArrayInArray,
          filterItems: filterItemsProgramTags,
          filterAllowNegative: true,
        },
        {
          name: 'treatmentArea',
          align: 'left',
          label: this.$t('programs.columnsTreatmentAreaLabel'),
          field: 'treatmentArea',
          classes: 'truncate-label-300',
          sort: (a, b, programA, programB) => {
            const programAOrder = chain(this.clinicTreatmentAreas)
              .find({ id: programA.treatmentArea })
              .get('order', 999)
              .value() * 1000
              + chain(this.clinicTreatmentAreas)
                .find({ id: programA.subTreatmentArea })
                .get('order', 0)
                .value();
            const programBOrder = chain(this.clinicTreatmentAreas)
              .find({ id: programB.treatmentArea })
              .get('order', 999)
              .value() * 1000
              + chain(this.clinicTreatmentAreas)
                .find({ id: programB.subTreatmentArea })
                .get('order', 0)
                .value();
            if (programAOrder === programBOrder) {
              const stringifiedA = programA.programName?.toString() || '';
              const stringifiedB = programB.programName?.toString() || '';

              stringifiedA.localeCompare(
                stringifiedB,
                undefined,
                { numeric: true, sensitivity: 'base' },
              );
            }
            return programAOrder > programBOrder ? 1 : -1;
          },
          format: (treatmentArea, program) => {
            const treatmentAreaLabel = chain(this.clinicTreatmentAreas)
              .find({ id: treatmentArea })
              .get('label')
              .value();
            if (!treatmentAreaLabel) {
              return '';
            }
            return chain([treatmentAreaLabel])
              .concat(chain(this.clinicTreatmentAreas)
                .find({ id: program.subTreatmentArea })
                .get('label')
                .value())
              .compact()
              .join(' - ')
              .value();
          },
          sortable: true,
        },
        {
          name: 'status',
          align: 'left',
          label: this.$t('programs.columnsStatusLabel'),
          field: 'status',
          sort: statusSortFunction,
          sortable: true,

          filterable: true,
          filterType: filterPropertyTypes.StringInArray,
          filterItems: this.programStatuses,
        },
        ...chain(this.visibleProgramDataFields)
          .sortBy('order')
          .map(
            (programDataField) => ({
              name: programDataField.id,
              label: programDataField.label,
              align: 'left',
              field: 'customData',
              format: (customData) => (this.formatCustomDataField(programDataField, customData)),
              sortable: false,
              mandatory: false,
              group: 'programData',

              filterable: true,
              filterType: this.getCustomDataFieldFilterType(programDataField),
              filterProperty: this.getCustomDataFieldFilterProperty(programDataField),
              ...this.getCustomDataFieldFilterItems(programDataField),
            }),
          )
          .value(),
      ];
    }, // programColumns()

    /**
     * @deprecated Use composables/columnDefinitions/programColumns.js instead
     */
    programColumnGroups() {
      return [
        {
          group: 'programData',
          label: this.$t('programs.columnGroupProgramData'),
        },
      ];
    },
  },

  methods: {
    getStatusIcon(status) {
      return programStatuses[status]?.icon || '';
    }, // getStatusIcon()

    getDefaultDataSeriesSettings(programId, programType, dataSeriesPreset = null) {
      const defaultGraphSettings = this.defaultGraphSettings[programType]
        ? { ...this.defaultGraphSettings[programType] } : {
          dataSource: 'dsSessionData',
          dataSeriesPreset: 'spSuccessRate',
          xAxis: 'xaDateDaily',
        };
      if (dataSeriesPreset) {
        defaultGraphSettings.dataSeriesPreset = dataSeriesPreset;
      }
      const seriesPresets = chain(this.dSources)
        .find({ id: defaultGraphSettings.dataSource })
        .get('seriesPresets')
        .find({ id: defaultGraphSettings.dataSeriesPreset })
        .omit('id')
        .value();
      const xAxis = chain(this.dSources)
        .find({ id: defaultGraphSettings.dataSource })
        .get('xAxes')
        .find({ id: defaultGraphSettings.xAxis })
        .omit('id')
        .value();

      const phaseLines = [
        {
          scope: 'program',
          program: programId || null,
          isSelected: true,
        },
      ];

      let postloadFilter = {};
      if (programType !== 'behaviour') {
        postloadFilter = {
          programs: {
            programId: [programId || null],
          },
        };
      }

      return {
        groupByDataProperty: 'programId',
        groupBySeriesLabelFrom: 'programName',
        additionalSettings: {},

        ...defaultGraphSettings,

        postloadFilter,
        preloadFilter: null,
        phaseLines,

        ...xAxis,
        ...seriesPresets,
      };
    }, // getDefaultDataSeriesSettings()

    /**
     * @param {Object} program
     * @param {string | null} clientId - id of the client we add activity to (needed to copy program from library)
     * @param {boolean} isDuplicated
     * @param {boolean} fromPackage - marks program installed from the package to the client
     * @return {Promise<void>}
     */
    async addProgramCreationActivity(
      program,
      clientId = null,
      isDuplicated = false,
      fromPackage = false,
    ) {
      const activityInfo = { ...this.programActivityInfo };

      if (clientId) {
        activityInfo.client = clientId;
      }

      const eventInfo = {
        programId: program.id,
        programName: program.programName,
        userName: this.userDetails.name,
        fromPackage,
      };

      if (isDuplicated) {
        const originalProgramName = replace(program.programName, ' (Copy)', '');
        eventInfo.notes = this.$t('activityFeed.notes.programCreationDuplicate', { originalProgramName });
      }

      return this.addActivity('programCreation', true, eventInfo, activityInfo, true, 'program');
    }, // addProgramCreationActivity()

    /**
     * @param {Object} program
     * @param {string} previousStatus
     * @param {string | null} newStatus
     * @return {Promise<void>}
     */
    async addProgramStatusModificationActivity(program, previousStatus, newStatus = null) {
      const eventInfo = {
        programId: program.id,
        programName: program.programName,
        userName: this.userDetails.name,
        statusChange: {
          previousStatus,
          currentStatus: newStatus || program.status,
        },
      };

      return this.addActivity('programModification', false, eventInfo, this.programActivityInfo, true, 'program');
    }, // addProgramStatusModificationActivity()

    setNewFilter(filterObj) {
      const processedFilter = {};
      const filterKeys = Object.keys(filterObj);
      filterKeys.forEach((key) => {
        const filterStatements = get(filterObj[key], 'filterStatements', null);
        if (filterStatements) {
          processedFilter[key] = filterObj[key];

          if (!some(filterStatements, (statement) => get(statement, 'customDataId'))) {
            processedFilter[key].filterStatements = map(filterStatements, (statement) => ({
              ...statement,
              customDataId: key,
            }));
          }
        } else {
          const { id, filterProperty, filterPropertyType, filterNegative, label, filterItems } = filterObj[key];
          processedFilter[key] = {
            id,
            filterProperty,
            filterPropertyType,
            filterStatements: [{
              filterNegative: filterNegative || false,
              label,
              filteredItems: filterItems,
            }],
          };
        }
      });
      return processedFilter;
    }, // setNewFilter()

    // @depricated Use composables/programs/programGraphs.js instead
    getProgramProgressSettings(program, defaultDateRangePreset = 'prev-90-days') {
      const programProgressSettings = get(program, 'progressSettings');
      if (programProgressSettings) {
        return {
          dateRange: this.applyDateRangePreset(programProgressSettings.dateRange),
          dataSeriesSettings: programProgressSettings.dataSeriesSettings,
        };
      }
      const primaryMetric = program.primaryMetric || programTypes[program.type]?.defaultMetric;
      const dataSeriesPreset = measurementMetrics
        .find((metric) => (metric.id === primaryMetric))?.dataSeriesPreset;

      return {
        dateRange: this.applyDateRangePreset({ preset: defaultDateRangePreset }),
        dataSeriesSettings: this.getDefaultDataSeriesSettings(program.id, program.type, dataSeriesPreset),
      };
    }, // getProgramProgressSettings()

    /**
     * Get an object with default target selection for programs added to session builder
     */
    getDefaultTargets(program) {
      const waiting = program.type === 'taskAnalysis';

      return {
        waiting,
        probe: true,
        acquisition: true,
        mastered: true,
        closed: false,
        hold: false,
        aborted: false,
      };
    }, // getDefaultTargets()

    /**
     * Bulk update programs and program targets and inform the user about the progress
     * @param {Object[]} updateItems - Array of update items
     * @param {string} updateItems[].type - The type of the update item, can be "program" or "programTarget"
     * @param {string} updateItems[].id - The ID of the update item
     * @param {string} [updateItems[].programId] - The program ID, only relevant if type is "programTarget"
     * @param {Object} updateItems[].changes - An object containing the changes to write
     * @returns {Promise<void>}
     */
    async updateProgramsAndTargets(updateItems) {
      this.$q.loading.show();

      try {
        const programsDb = new ProgramsDB();
        const programTargetsDb = new ProgramTargetsDB();

        const updateItemChunks = chunk(updateItems, 25);
        const totalChunks = updateItemChunks.length;
        let processedChunks = 0;
        // eslint-disable-next-line no-restricted-syntax
        for (const updateItemChunk of updateItemChunks) {
          const progress = Math.round((processedChunks / totalChunks) * 100);
          this.$q.loading.show({
            message: `${progress}%`,
          });
          const updateItemActions = updateItemChunk.map((item) => {
            if (item.type === 'programTarget') {
              programTargetsDb.setProgramId(item.programId);
              return programTargetsDb.set(item.changes, item.id);
            }
            return programsDb.set(item.changes, item.id);
          });
          // eslint-disable-next-line no-await-in-loop
          await Promise.all(updateItemActions);
          processedChunks += 1;
        }
      } catch (err) {
        logEvent(err);
        console.error(err);
        this.notifyNegative(this.$t('common.errors.genericAccessDenied'));
      } finally {
        this.$q.loading.hide();
      }
    }, // updateProgramsWithNewWorkflow()

  },
};
